import { API_URL } from 'config';
import React, { useEffect, useMemo, useState } from 'react';
import './AdminPage.css';

const API = `${API_URL}`;

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState('stats');
  const [isAdmin, setIsAdmin] = useState(null);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [prices, setPrices] = useState(null);
  const [promoCodes, setPromoCodes] = useState([]);
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [newLicense, setNewLicense] = useState({ user_id: '', license_type: 'monthly', days: '30', key: '', modules: [] });
  const [newModule, setNewModule] = useState({ module_id: '', name: '', price: '', features: '', order: 0, is_active: true });
  const [newPromo, setNewPromo] = useState({ code: '', name: '', discount_percent: '', is_active: true });

  useEffect(() => {
    const checkAdmin = async () => {
      const res = await request('/admin/stats');
      if (res.status === 403 || res.status === 401) {
        setIsAdmin(false);
        return;
      }
      setIsAdmin(true);
      setStats(res);
    };
    checkAdmin();
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    if (activeTab === 'stats') fetchStats();
    if (activeTab === 'users') {
      fetchUsers();
      fetchPrices();
    }
    if (activeTab === 'referrals') fetchReferrals();
    if (activeTab === 'prices') fetchPrices();
    if (activeTab === 'discounts') fetchPromoCodes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isAdmin]);

  const request = async (url, options = {}) => {
    const response = await fetch(`${API}${url}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      ...options,
    });
    const data = await response.json().catch(() => ({}));
    return { status: response.status, ...data };
  };

  const showMessage = (text) => {
    setMessage(text);
    setTimeout(() => setMessage(''), 3000);
  };

  const fetchStats = async () => {
    const res = await request('/admin/stats');
    if (res.status === 'ok') setStats(res);
  };

  const fetchUsers = async () => {
    const res = await request('/admin/users?limit=500');
    if (res.status === 'ok') setUsers(res.users || []);
  };

  const fetchReferrals = async () => {
    const res = await request('/admin/referrals');
    if (res.status === 'ok') setReferrals(res.referrals || []);
  };

  const fetchPrices = async () => {
    const res = await request('/admin/prices');
    if (res.status === 'ok') setPrices(res);
  };

  const fetchPromoCodes = async () => {
    const res = await request('/admin/promo-codes');
    if (res.status === 'ok') setPromoCodes(res.promo_codes || []);
  };

  const handleResponse = (res, successText, refresh) => {
    if (res.status === 'ok') {
      showMessage(successText);
      refresh();
    } else {
      showMessage(res.detail || 'Error');
    }
  };

  const updateLicensePrice = async (productType, price, isActive) => {
    const res = await request('/admin/prices/license', {
      method: 'POST',
      body: JSON.stringify({ product_type: productType, price: parseFloat(price), is_active: isActive }),
    });
    handleResponse(res, 'Saved', fetchPrices);
  };

  const updateModule = async (module) => {
    const res = await request('/admin/prices/module', {
      method: 'POST',
      body: JSON.stringify(module),
    });
    handleResponse(res, 'Saved', fetchPrices);
  };

  const deleteModule = async (moduleId) => {
    if (!window.confirm('Delete module?')) return;
    const res = await request(`/admin/prices/module/${moduleId}`, { method: 'DELETE' });
    handleResponse(res, 'Deleted', fetchPrices);
  };

  const createModule = async () => {
    const res = await request('/admin/prices/module', {
      method: 'POST',
      body: JSON.stringify({
        ...newModule,
        price: parseFloat(newModule.price),
        features: newModule.features.split('\n').map((s) => s.trim()).filter(Boolean),
        order: parseInt(newModule.order, 10) || 0,
      }),
    });
    if (res.status === 'ok') {
      showMessage('Module created');
      setNewModule({ module_id: '', name: '', price: '', features: '', order: 0, is_active: true });
      fetchPrices();
    } else {
      showMessage(res.detail || 'Error');
    }
  };

  const createPromoCode = async () => {
    const res = await request('/admin/promo-codes', {
      method: 'POST',
      body: JSON.stringify({
        ...newPromo,
        discount_percent: parseInt(newPromo.discount_percent, 10),
      }),
    });
    if (res.status === 'ok') {
      showMessage('Promo code created');
      setNewPromo({ code: '', name: '', discount_percent: '', is_active: true });
      fetchPromoCodes();
    } else {
      showMessage(res.detail || 'Error');
    }
  };

  const deletePromoCode = async (promoId) => {
    if (!window.confirm('Delete promo code?')) return;
    const res = await request(`/admin/promo-codes/${promoId}`, { method: 'DELETE' });
    handleResponse(res, 'Deleted', fetchPromoCodes);
  };

  const resetReferralBalance = async (userId) => {
    if (!window.confirm('Reset referral balance?')) return;
    const res = await request(`/admin/referrals/${userId}/reset`, { method: 'POST' });
    handleResponse(res, 'Reset', fetchReferrals);
  };

  const updateReferralSettings = async (e) => {
    e.preventDefault();
    const form = e.target;
    const res = await request('/admin/referral-settings', {
      method: 'POST',
      body: JSON.stringify({
        discount_percent: parseInt(form.discount.value, 10),
        reward_percent: parseInt(form.reward.value, 10),
        code_length: parseInt(form.code_length.value, 10),
        code_alphabet: form.code_alphabet.value,
      }),
    });
    handleResponse(res, 'Saved', fetchReferrals);
  };

  const updateUserReferralPercent = async (userId, field, value) => {
    const payload = {};
    payload[field] = value === '' ? null : parseInt(value, 10);
    const res = await request(`/admin/users/${userId}/referral-percent`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    handleResponse(res, 'Saved', fetchUsers);
  };

  const updateUserField = async (userId, field, value) => {
    const res = await request(`/admin/users/${userId}`, {
      method: 'POST',
      body: JSON.stringify({ [field]: value }),
    });
    handleResponse(res, 'Saved', fetchUsers);
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Delete user and all related data?')) return;
    const res = await request(`/admin/users/${userId}`, { method: 'DELETE' });
    handleResponse(res, 'Deleted', fetchUsers);
  };

  const updateLicenseKey = async (licenseId, newKey) => {
    const res = await request(`/admin/licenses/${licenseId}/key`, {
      method: 'POST',
      body: JSON.stringify({ key: newKey }),
    });
    handleResponse(res, 'Key updated', fetchUsers);
  };

  const updateLicenseModules = async (licenseId, modules) => {
    const res = await request(`/admin/licenses/${licenseId}/modules`, {
      method: 'POST',
      body: JSON.stringify({ modules }),
    });
    handleResponse(res, 'Modules updated', fetchUsers);
  };

  const resetLicenseHwid = async (licenseId) => {
    const res = await request(`/admin/licenses/${licenseId}/reset-hwid`, { method: 'POST' });
    handleResponse(res, 'HWID reset', fetchUsers);
  };

  const updateLicenseExpires = async (licenseId, days) => {
    const res = await request(`/admin/licenses/${licenseId}/expires`, {
      method: 'POST',
      body: JSON.stringify({ days: parseInt(days, 10) }),
    });
    handleResponse(res, 'Expires updated', fetchUsers);
  };

  const deleteLicense = async (licenseId) => {
    if (!window.confirm('Delete license?')) return;
    const res = await request(`/admin/licenses/${licenseId}`, { method: 'DELETE' });
    handleResponse(res, 'Deleted', fetchUsers);
  };

  const createLicense = async () => {
    const res = await request('/admin/licenses', {
      method: 'POST',
      body: JSON.stringify({
        ...newLicense,
        user_id: parseInt(newLicense.user_id, 10),
        days: newLicense.days ? parseInt(newLicense.days, 10) : null,
        modules: newLicense.modules,
      }),
    });
    if (res.status === 'ok') {
      showMessage('License created');
      setNewLicense({ user_id: '', license_type: 'monthly', days: '30', key: '', modules: [] });
      fetchUsers();
    } else {
      showMessage(res.detail || 'Error');
    }
  };

  const generateKey = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let key = '';
    for (let i = 0; i < 32; i++) key += chars.charAt(Math.floor(Math.random() * chars.length));
    setNewLicense((prev) => ({ ...prev, key }));
  };

  const toggleModule = (moduleId, list, setList) => {
    if (list.includes(moduleId)) {
      setList(list.filter((m) => m !== moduleId));
    } else {
      setList([...list, moduleId]);
    }
  };

  const filteredUsers = useMemo(() => {
    if (!search) return users;
    const q = search.toLowerCase();
    return users.filter(
      (u) =>
        u.email?.toLowerCase().includes(q) ||
        String(u.id).includes(q) ||
        u.referral_code?.toLowerCase().includes(q)
    );
  }, [users, search]);

  const moduleOptions = useMemo(() => prices?.modules || [], [prices]);

  if (isAdmin === false) {
    return (
      <div className="admin-page">
        <div className="admin-access-denied">
          <h2>Access denied</h2>
          <p>You are not an admin.</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-page">
        <div className="admin-loading">Loading...</div>
      </div>
    );
  }

  const tabs = [
    { id: 'stats', label: 'Stats', icon: 'fa-chart-pie' },
    { id: 'users', label: 'Users', icon: 'fa-users' },
    { id: 'referrals', label: 'Referrals', icon: 'fa-gift' },
    { id: 'prices', label: 'Keys & Modules', icon: 'fa-key' },
    { id: 'discounts', label: 'Discounts', icon: 'fa-percent' },
  ];

  return (
    <div className="admin-page">
      <div className="admin-sidebar">
        <h2 className="admin-logo">Prime Admin</h2>
        <nav className="admin-nav">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`admin-nav-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <i className={`fas ${tab.icon}`}></i>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>
      <div className="admin-content">
        {message && <div className="admin-message">{message}</div>}

        {activeTab === 'stats' && stats && (
          <div className="admin-section">
            <h3 className="admin-section-title">Statistics</h3>
            <div className="admin-cards">
              <div className="admin-card">
                <div className="admin-card-title">Total users</div>
                <div className="admin-card-value">{stats.total_users}</div>
              </div>
              <div className="admin-card">
                <div className="admin-card-title">Demo keys</div>
                <div className="admin-card-value">{stats.demo_keys}</div>
              </div>
              <div className="admin-card">
                <div className="admin-card-title">Module purchases</div>
                <div className="admin-card-value">{stats.module_purchases}</div>
              </div>
              <div className="admin-card">
                <div className="admin-card-title">Referral payouts</div>
                <div className="admin-card-value">${stats.referral_payouts}</div>
              </div>
              <div className="admin-card">
                <div className="admin-card-title">Referral link visits</div>
                <div className="admin-card-value">{stats.referral_clicks ?? 0}</div>
              </div>
            </div>
            <h4 className="admin-subtitle">Licenses by type</h4>
            <div className="admin-type-grid">
              {Object.entries(stats.licenses_by_type || {}).map(([type, count]) => (
                <div key={type} className="admin-type-chip">
                  <span className="admin-type-name">{type}</span>
                  <span className="admin-type-count">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="admin-section">
            <div className="admin-section-header">
              <h3 className="admin-section-title">Users</h3>
              <input
                className="admin-search-input"
                placeholder="Search by email, ID or code..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="admin-user-list">
              {filteredUsers.map((user) => (
                <div key={user.id} className="admin-user-card">
                  <div className="admin-user-header" onClick={() => setExpandedUser(expandedUser === user.id ? null : user.id)}>
                    <div className="admin-user-info">
                      <strong>{user.email}</strong>
                      <span className="admin-user-meta">ID: {user.id} · {user.referral_code}</span>
                    </div>
                    <div className="admin-user-badges">
                      {user.is_admin && <span className="admin-badge admin-badge-admin">Admin</span>}
                      <span className={`admin-badge ${user.used_demo ? 'active' : ''}`}>Demo</span>
                      <span className="admin-badge">Licenses: {user.licenses?.length || 0}</span>
                      <i className={`fas fa-chevron-${expandedUser === user.id ? 'up' : 'down'} admin-expand-icon`}></i>
                    </div>
                  </div>
                  {expandedUser === user.id && (
                    <div className="admin-user-body">
                      <div className="admin-user-controls">
                        <label className="admin-control">
                          <span>Admin</span>
                          <input
                            type="checkbox"
                            checked={!!user.is_admin}
                            onChange={(e) => updateUserField(user.id, 'is_admin', e.target.checked)}
                          />
                        </label>
                        <label className="admin-control">
                          <span>Used demo</span>
                          <input
                            type="checkbox"
                            checked={!!user.used_demo}
                            onChange={(e) => updateUserField(user.id, 'used_demo', e.target.checked)}
                          />
                        </label>
                        <button className="admin-btn danger" onClick={() => deleteUser(user.id)}>
                          <i className="fas fa-trash"></i> Delete user
                        </button>
                      </div>

                      <div className="admin-licenses">
                        <h5>Licenses</h5>
                        {(user.licenses || []).map((lic) => (
                          <div key={lic.id} className="admin-license-card">
                            <div className="admin-license-row">
                              <div className="admin-form-group">
                                <label>Key</label>
                                <input
                                  className="admin-input"
                                  defaultValue={lic.key}
                                  onBlur={(e) => updateLicenseKey(lic.id, e.target.value)}
                                />
                              </div>
                              <div className="admin-form-group small">
                                <label>Type</label>
                                <span className="admin-readonly">{lic.type}</span>
                              </div>
                              <div className="admin-form-group small">
                                <label>Expires</label>
                                <span className="admin-readonly">{lic.expires_at ? new Date(lic.expires_at).toLocaleString() : 'Never'}</span>
                              </div>
                            </div>
                            <div className="admin-license-row">
                              <div className="admin-form-group">
                                <label>Set days</label>
                                <input
                                  className="admin-input"
                                  type="number"
                                  placeholder="Days from now"
                                  onBlur={(e) => updateLicenseExpires(lic.id, e.target.value)}
                                />
                              </div>
                              <div className="admin-form-group modules">
                                <label>Modules</label>
                                <div className="admin-module-checks">
                                  {moduleOptions.map((m) => (
                                    <label key={m.module_id} className="admin-check">
                                      <input
                                        type="checkbox"
                                        checked={(lic.modules || []).includes(m.module_id)}
                                        onChange={() => {
                                          const updated = (lic.modules || []).includes(m.module_id)
                                            ? (lic.modules || []).filter((id) => id !== m.module_id)
                                            : [...(lic.modules || []), m.module_id];
                                          updateLicenseModules(lic.id, updated);
                                        }}
                                      />
                                      <span>{m.name}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            </div>
                            <div className="admin-license-actions">
                              <button className="admin-btn" onClick={() => resetLicenseHwid(lic.id)}>
                                <i className="fas fa-undo"></i> Reset HWID
                              </button>
                              <button className="admin-btn danger" onClick={() => deleteLicense(lic.id)}>
                                <i className="fas fa-trash"></i> Delete license
                              </button>
                            </div>
                          </div>
                        ))}
                        <div className="admin-add-license">
                          <h5>Add license</h5>
                          <div className="admin-form-row">
                            <input
                              className="admin-input"
                              placeholder="User ID"
                              value={newLicense.user_id}
                              onChange={(e) => setNewLicense({ ...newLicense, user_id: e.target.value })}
                            />
                            <select
                              className="admin-input"
                              value={newLicense.license_type}
                              onChange={(e) => setNewLicense({ ...newLicense, license_type: e.target.value })}
                            >
                              <option value="demo">Demo</option>
                              <option value="monthly">Monthly</option>
                              <option value="permanent">Permanent</option>
                            </select>
                            <input
                              className="admin-input"
                              placeholder="Days"
                              value={newLicense.days}
                              onChange={(e) => setNewLicense({ ...newLicense, days: e.target.value })}
                            />
                          </div>
                          <div className="admin-form-row">
                            <input
                              className="admin-input key-input"
                              placeholder="License key"
                              value={newLicense.key}
                              onChange={(e) => setNewLicense({ ...newLicense, key: e.target.value })}
                            />
                            <button className="admin-btn" onClick={generateKey}>
                              <i className="fas fa-sync"></i> Generate
                            </button>
                          </div>
                          <div className="admin-module-checks">
                            {moduleOptions.map((m) => (
                              <label key={m.module_id} className="admin-check">
                                <input
                                  type="checkbox"
                                  checked={newLicense.modules.includes(m.module_id)}
                                  onChange={() => toggleModule(m.module_id, newLicense.modules, (mods) => setNewLicense({ ...newLicense, modules: mods }))}
                                />
                                <span>{m.name}</span>
                              </label>
                            ))}
                          </div>
                          <button className="admin-btn primary" onClick={createLicense}>
                            <i className="fas fa-plus"></i> Create license
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'referrals' && (
          <div className="admin-section">
            <h3 className="admin-section-title">Referral settings</h3>
            <form className="admin-form" onSubmit={updateReferralSettings}>
              <label>
                <span>Discount %</span>
                <input name="discount" type="number" defaultValue={5} className="admin-input" />
              </label>
              <label>
                <span>Reward %</span>
                <input name="reward" type="number" defaultValue={5} className="admin-input" />
              </label>
              <label>
                <span>Code length</span>
                <input name="code_length" type="number" defaultValue={8} className="admin-input" />
              </label>
              <label>
                <span>Code alphabet</span>
                <input name="code_alphabet" type="text" defaultValue="ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789" className="admin-input" />
              </label>
              <button type="submit" className="admin-btn primary">Save settings</button>
            </form>

            <h3 className="admin-section-title">Top referrers</h3>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Code</th>
                    <th>Balance</th>
                    <th>Total earned</th>
                    <th>Referrals</th>
                    <th>Link visits</th>
                    <th>Personal discount %</th>
                    <th>Personal reward %</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((ref) => (
                    <tr key={ref.id}>
                      <td>{ref.email}</td>
                      <td>{ref.referral_code}</td>
                      <td>${ref.balance}</td>
                      <td>${ref.total_earned}</td>
                      <td>{ref.referrals_count}</td>
                      <td>{ref.clicks_count ?? 0}</td>
                      <td>
                        <input
                          className="admin-input small"
                          type="number"
                          defaultValue={ref.personal_discount_percent ?? ''}
                          placeholder="Global"
                          onBlur={(e) => updateUserReferralPercent(ref.id, 'personal_discount_percent', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="admin-input small"
                          type="number"
                          defaultValue={ref.personal_reward_percent ?? ''}
                          placeholder="Global"
                          onBlur={(e) => updateUserReferralPercent(ref.id, 'personal_reward_percent', e.target.value)}
                        />
                      </td>
                      <td>
                        <button className="admin-btn" onClick={() => resetReferralBalance(ref.id)}>
                          Reset balance
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'prices' && prices && (
          <div className="admin-section">
            <h3 className="admin-section-title">License prices</h3>
            <div className="admin-price-list">
              {prices.license_prices && prices.license_prices.map((p) => (
                <div key={p.product_type} className="admin-price-row">
                  <span className="admin-price-name">{p.product_type}</span>
                  <input
                    className="admin-input"
                    type="number"
                    defaultValue={p.price}
                    onBlur={(e) => updateLicensePrice(p.product_type, e.target.value)}
                  />
                  <span className="admin-price-active">
                    <input
                      type="checkbox"
                      defaultChecked={p.is_active}
                      onChange={(e) => updateLicensePrice(p.product_type, p.price, e.target.checked)}
                    />
                    Active
                  </span>
                </div>
              ))}
            </div>

            <h3 className="admin-section-title">Modules</h3>
            <div className="admin-module-list">
              {prices.modules && prices.modules.map((m) => (
                <div key={m.module_id} className="admin-module-card">
                  <input
                    className="admin-input"
                    defaultValue={m.module_id}
                    onBlur={(e) => updateModule({ ...m, module_id: e.target.value })}
                    disabled
                  />
                  <input
                    className="admin-input"
                    defaultValue={m.name}
                    onBlur={(e) => updateModule({ ...m, name: e.target.value })}
                  />
                  <input
                    className="admin-input"
                    type="number"
                    defaultValue={m.price}
                    onBlur={(e) => updateModule({ ...m, price: parseFloat(e.target.value) })}
                  />
                  <input
                    className="admin-input"
                    type="number"
                    defaultValue={m.order}
                    onBlur={(e) => updateModule({ ...m, order: parseInt(e.target.value, 10) })}
                  />
                  <label className="admin-check inline">
                    <input
                      type="checkbox"
                      defaultChecked={m.is_active}
                      onChange={(e) => updateModule({ ...m, is_active: e.target.checked })}
                    />
                    <span>Active</span>
                  </label>
                  <button className="admin-btn danger" onClick={() => deleteModule(m.module_id)}>
                    Delete
                  </button>
                </div>
              ))}
            </div>

            <div className="admin-add-module">
              <h5>Add module</h5>
              <div className="admin-form-row">
                <input
                  className="admin-input"
                  placeholder="Module ID"
                  value={newModule.module_id}
                  onChange={(e) => setNewModule({ ...newModule, module_id: e.target.value })}
                />
                <input
                  className="admin-input"
                  placeholder="Name"
                  value={newModule.name}
                  onChange={(e) => setNewModule({ ...newModule, name: e.target.value })}
                />
                <input
                  className="admin-input"
                  type="number"
                  placeholder="Price"
                  value={newModule.price}
                  onChange={(e) => setNewModule({ ...newModule, price: e.target.value })}
                />
                <input
                  className="admin-input"
                  type="number"
                  placeholder="Order"
                  value={newModule.order}
                  onChange={(e) => setNewModule({ ...newModule, order: e.target.value })}
                />
              </div>
              <textarea
                className="admin-textarea"
                placeholder="Features (one per line)"
                value={newModule.features}
                onChange={(e) => setNewModule({ ...newModule, features: e.target.value })}
              />
              <button className="admin-btn primary" onClick={createModule}>
                <i className="fas fa-plus"></i> Add module
              </button>
            </div>
          </div>
        )}

        {activeTab === 'discounts' && (
          <div className="admin-section">
            <h3 className="admin-section-title">Promo codes</h3>
            <div className="admin-add-module">
              <h5>Add promo code</h5>
              <div className="admin-form-row">
                <input
                  className="admin-input"
                  placeholder="Code"
                  value={newPromo.code}
                  onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                />
                <input
                  className="admin-input"
                  placeholder="Name (optional)"
                  value={newPromo.name}
                  onChange={(e) => setNewPromo({ ...newPromo, name: e.target.value })}
                />
                <input
                  className="admin-input"
                  type="number"
                  placeholder="Discount %"
                  value={newPromo.discount_percent}
                  onChange={(e) => setNewPromo({ ...newPromo, discount_percent: e.target.value })}
                />
                <label className="admin-check inline">
                  <input
                    type="checkbox"
                    checked={newPromo.is_active}
                    onChange={(e) => setNewPromo({ ...newPromo, is_active: e.target.checked })}
                  />
                  <span>Active</span>
                </label>
              </div>
              <button className="admin-btn primary" onClick={createPromoCode}>
                <i className="fas fa-plus"></i> Add promo code
              </button>
            </div>

            <h3 className="admin-section-title">Active codes</h3>
            <div className="admin-module-list">
              {promoCodes.map((p) => (
                <div key={p.id} className="admin-module-card">
                  <input className="admin-input" value={p.code} disabled />
                  <input className="admin-input" value={p.name || ''} disabled />
                  <input className="admin-input" type="number" value={p.discount_percent} disabled />
                  <label className="admin-check inline">
                    <input type="checkbox" checked={p.is_active} disabled />
                    <span>Active</span>
                  </label>
                  <button className="admin-btn danger" onClick={() => deletePromoCode(p.id)}>
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
